import React from 'react';
import { walkClaimText } from '../../../../lib/walkClaimText';
import type { SiteRagReport } from '../../../../types/siteRag';
import { SectionShell } from '../SectionShell';
import { PowerIntentIntroStrip } from './PowerIntentIntroStrip';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { EditorialSubsection, EditorialCallout } from '../../report/editorial';
import { resolveSectionCallout } from '../../../../lib/reportRendering/reportSectionCallouts';

export const InterconnectionQueueSection: React.FC<{ report: SiteRagReport; label: string }> = ({ report, label }) => {
  const theme = useEffectiveReportTheme();
  const text = report.powerAnalysis?.interconnectionQueue;
  const callout = resolveSectionCallout(report, 'interconnectionQueue');
  const isLoadServicePathway = typeof text === 'string' && /Member Project Request\b/i.test(text);
  const hasMprCostContext =
    typeof text === 'string'
    && (text.includes('133.7 million') || text.includes('MPR-26-HW05'));
  return (
    <SectionShell label={label} hasData={!!text}>
      <PowerIntentIntroStrip report={report} focus="timeline" />
      {callout && (
        <EditorialCallout theme={theme} tone={callout.tone} label={callout.label} style={{ marginTop: 8 }}>
          {callout.body}
        </EditorialCallout>
      )}
      {text && (
        <EditorialSubsection
          theme={theme}
          label={isLoadServicePathway ? 'Load Service & Interconnection Assessment' : 'Queue assessment'}
        >
          {walkClaimText(text, report.references)}
          {hasMprCostContext && (
            <p
              data-testid="interconnection-cost-boundary-note"
              style={{
                marginTop: 10,
                fontSize: 11.5,
                lineHeight: 1.5,
                color: (theme as { mutedTextColor?: string }).mutedTextColor ?? '#475569',
              }}
            >
              <strong>Commercial, Scope, Security &amp; Time-Horizon Boundary Note:</strong>{' '}
              The developer-reported <strong>$133.7M</strong> (~$134M rounded) figure is a Class 5 planning estimate from an older 1,000 MW network study ($51.6M MPR 345 kV four-position ring-bus switching station plus $82.1M transmission/reactive upgrades) that <em>excludes</em> the customer delivery-point substation, whereas the <strong>$3–8M</strong> figure is a generic single-line HIFLD distance heuristic and not a comparable substitute for the 1,000 MW network buildout. The indicative Tallgrass <strong>$8.03M/yr</strong> (365-day basis; $8.0355M/yr on a 365.25-day leap-year basis) figure is a conditional pipeline lateral reservation charge (200,000 dth/day minimum volume commitment × $0.110/dth/day over a 20-year primary term vs. a 31-year project analysis horizon), excluding commodity gas, upstream transport, and BTM generation capex/O&amp;M. The <strong>$250,000</strong> Tri-State HIL study fee is a non-refundable study deposit (October 28, 2026 participation deadline) distinct from future transmission or pipeline credit-support / capacity-security postings, which are collateral instruments and must not be double-counted as direct construction capex.
            </p>
          )}
        </EditorialSubsection>
      )}
    </SectionShell>
  );
};
